import { GD } from "src/GD";
import { requestListPage } from "./listPage";
import { refreshBalanceSources } from "./refreshBalanceSources";
import { getIssuerRequestContext, isIssuerRequestContextCurrent } from "./issuerRequestContext";
import { recordFreshUserBalanceOverride } from "./freshUserBalanceOverrides";

const DEFAULT_TRANSACTIONS_RESPONSE: ICompanyTransactionsResponse = {
	transactions: [],
	pagination: { total: 0, page: 0, limit: 20, pages: 0 },
	balances: {},
};

const ALL_TRANSACTIONS_METHOD = "companies/transactions/all";

const normalizeNullableNumber = (value: unknown) =>
	value === null || value === undefined || value === "" ? null : Number(value);

const normalizeParentTransaction = (
	parent?: ICompanyTrxParentTransaction | null
): ICompanyTrxParentTransaction | undefined =>
	parent
		? {
				...parent,
				amount: Number(parent.amount),
				operation_id: normalizeNullableNumber(parent.operation_id),
			}
		: undefined;

const normalizeTransaction = (transaction: ICompanyTrxTransaction): ICompanyTrxTransaction => ({
	...transaction,
	id: Number(transaction.id),
	operation_id: normalizeNullableNumber(transaction.operation_id),
	type_id: Number(transaction.type_id),
	amount: transaction.amount,
	balance: transaction.balance,
	account_id: Number(transaction.account_id),
	counterparty_account_id: Number(transaction.counterparty_account_id),
	parent: normalizeParentTransaction(transaction.parent),
});

const normalizeTransactionsResponse = (response?: Partial<ICompanyTransactionsResponse> | null): ICompanyTransactionsResponse => {
	if (!response) return DEFAULT_TRANSACTIONS_RESPONSE;

	return {
		transactions: Array.isArray(response.transactions)
			? response.transactions.map(transaction => normalizeTransaction(transaction as ICompanyTrxTransaction))
			: [],
		pagination: response.pagination
			? {
					total: Number(response.pagination.total ?? 0),
					page: Number(response.pagination.page ?? 0),
					limit: Number(response.pagination.limit ?? 20),
					pages: Number(response.pagination.pages ?? 0),
				}
			: DEFAULT_TRANSACTIONS_RESPONSE.pagination,
		balances: response.balances ?? {},
	};
};

export class TeamsService {
	private teamsListSequence = 0;

	constructor() {
		GD.REQ_TEAMS_LIST.listener = this.getTeams;
		GD.REQ_TEAMS_PAGE.listener = query => requestListPage<ITeamSaved, ITeamListSummary>("teams/list", query);
		GD.REQ_TEAM_CREATE.listener = this.createTeam;
		GD.REQ_TEAM_UPDATE.listener = this.updateTeam;
		GD.REQ_TEAM_GET.listener = this.getTeam;
		GD.REQ_TEAM_USERS.listener = this.getTeamUsers;
		GD.REQ_TEAM_TRANSACTIONS.listener = this.getTeamTransactions;
		GD.REQ_TEAM_STATS_MONEY_INFLOW.listener = this.getTeamMoneyInflow;
		GD.REQ_TEAM_STATS_TOP_SPENDERS.listener = this.getTeamTopSpenders;
		GD.REQ_TEAM_STATS_TRANSACTION_STATUS.listener = this.getTeamTransactionStatus;
		GD.REQ_TEAM_USER_ADD.listener = this.addTeamUser;
		GD.REQ_TEAM_USER_DISABLE.listener = this.disableTeamUser;
		GD.REQ_USERS_LIST_TEAM.listener = this.getUsersForTeam;
		GD.REQ_TEAM_USER_TOPUP.listener = this.topUpTeamUser;
		GD.REQ_TEAM_USER_WITHDRAW.listener = this.withdrawTeamUser;
	}

	private requestTeamTransactions = async (method: string, data?: unknown): Promise<IAPIResponse<ICompanyTransactionsResponse>> => {
		const response = await GD.REQ_API.request({
			method,
			httpMethod: "POST",
			data: data ?? {},
		});

		if (response.error) return { error: response.error };

		return { data: normalizeTransactionsResponse(response.data as ICompanyTransactionsResponse) };
	};

	private topUpTeamUser: (data: { user_uid: string; uid: string; amount: number; currency: string; issuer?: number }) => Promise<IAPIResponse<IStatusResponse>> =
		async data => {
			const res = await GD.REQ_API.request({
				httpMethod: "POST",
				method: "teams/user/topup",
				data,
			});

			if (!res.error) {
				await refreshBalanceSources();
			}

			return res as IAPIResponse<IStatusResponse>;
		};

	private withdrawTeamUser: (data: ITeamUserWithdrawRequest) => Promise<IAPIResponse<ITeamUserWithdrawResponse>> =
		async data => {
			const payload: ITeamUserWithdrawRequest = {};
			if (data?.user_uid) payload.user_uid = data.user_uid;
			if (data?.uid) payload.uid = data.uid;
			if (typeof data?.amount === "number") payload.amount = data.amount;
			if (data?.currency) payload.currency = data.currency;
			if (data?.issuer !== undefined) payload.issuer = data.issuer;

			const res = await GD.REQ_API.request({
				httpMethod: "POST",
				method: "teams/user/withdraw",
				data: payload,
			});

			if (!res.error && (res.data as ITeamUserWithdrawResponse | undefined)?.status === true) {
				const response = res.data as ITeamUserWithdrawResponse;
				if (data.user_uid) {
					recordFreshUserBalanceOverride({
						userUid: data.user_uid,
						issuer: data.issuer,
						currency: data.currency || "USD",
						balance: response.balance,
					});
				}
				await refreshBalanceSources();
			}

			return res as IAPIResponse<ITeamUserWithdrawResponse>;
		};

	private getTeams: (options?: { force?: boolean; user_uid?: string } | Record<string, unknown> | void) => Promise<IAPIResponse<ITeamSaved[]>> =
		async options => {
			const opts = options as { force?: boolean; user_uid?: string; issuer?: number } | undefined;
			const publishesCompanyList = !opts?.user_uid;
			const requestSequence = publishesCompanyList ? ++this.teamsListSequence : 0;
			const requestContext = getIssuerRequestContext();
			const params: Record<string, any> = {};

			if (opts?.user_uid) {
				params.user_uid = opts.user_uid;
			}
			if (opts?.issuer !== undefined) params.issuer = opts.issuer;

			const res = await GD.REQ_API.request({
				httpMethod: "GET",
				method: "teams/list",
				params,
			});

			if (
				!res.error &&
				res.data &&
				publishesCompanyList &&
				requestSequence === this.teamsListSequence &&
				isIssuerRequestContextCurrent(requestContext)
			) {
				GD.S_TEAMS_LIST_UPDATED.invoke(res.data as ITeamSaved[]);
			}

			return res as IAPIResponse<ITeamSaved[]>;
		};

	private createTeam: (data: ITeamRaw) => Promise<IAPIResponse<ITeamSaved>> = async data => {
		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "teams/create",
			data,
		});

		if (!res.error) {
			await this.getTeams();
		}

		return res as IAPIResponse<ITeamSaved>;
	};

	private updateTeam: (data: ITeamSaved) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "teams/update",
			data,
		});

		if (!res.error) {
			await this.getTeams();
		}

		return res as IAPIResponse<IStatusResponse>;
	};

	private getTeam: (uid: string) => Promise<IAPIResponse<ITeamSaved>> = async uid => {
		if (!uid) return { data: null } as unknown as IAPIResponse<ITeamSaved>;

		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: `teams/get?uid=${uid}`,
		});

		return res as IAPIResponse<ITeamSaved>;
	};

	private getTeamUsers: (data: { uid: string; issuer?: number }) => Promise<IAPIResponse<ITeamUser[]>> = async data => {
		if (!data?.uid) return { data: [] } as IAPIResponse<ITeamUser[]>;

		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: `teams/users?uid=${data?.uid}`,
			params: data.issuer === undefined ? undefined : { issuer: data.issuer },
		});

		return res as IAPIResponse<ITeamUser[]>;
	};

	private getTeamTransactions: (data: { uid: string } & ICompanyTransactionFilter) => Promise<IAPIResponse<ICompanyTransactionsResponse>> = async data => {
		if (!data?.uid) return { data: DEFAULT_TRANSACTIONS_RESPONSE };
		const { uid, ...filters } = data;
		return this.requestTeamTransactions(ALL_TRANSACTIONS_METHOD, {
			...filters,
			team_uid: uid,
		});
	};

	private getTeamMoneyInflow: (data: { uid: string; days?: number; currency?: string }) => Promise<IAPIResponse<ITeamMoneyInflowStats>> = async data => {
		if (!data?.uid) {
			return { data: { days: Number(data?.days ?? 7), currency: String(data?.currency ?? "USD"), total: 0, daily: [] } };
		}

		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "teams/stats/money-inflow",
			data,
		});

		return res as IAPIResponse<ITeamMoneyInflowStats>;
	};

	private getTeamTopSpenders: (data: { uid: string; limit?: number; currency?: string; entity?: Exclude<TCompanyTransactionEntity, "all">; tx_type?: TCompanyTransactionType }) => Promise<IAPIResponse<ITeamTopSpendersStats>> = async data => {
		if (!data?.uid) {
			return { data: { limit: Number(data?.limit ?? 5), currency: String(data?.currency ?? "USD"), total: 0, buyers: [] } };
		}

		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "teams/stats/top-spenders",
			data,
		});

		return res as IAPIResponse<ITeamTopSpendersStats>;
	};

	private getTeamTransactionStatus: (data: { uid: string; days?: number; currency?: string }) => Promise<IAPIResponse<ITeamTransactionStatusStats>> =
		async data => {
			if (!data?.uid) {
				return {
					data: {
						days: Number(data?.days ?? 7),
						currency: String(data?.currency ?? "USD"),
						total_count: 0,
						total_amount: 0,
						statuses: [],
					},
				};
			}

			const res = await GD.REQ_API.request({
				httpMethod: "POST",
				method: "teams/stats/transaction-status",
				data,
			});

			return res as IAPIResponse<ITeamTransactionStatusStats>;
		};

	private addTeamUser: (data: { user_uid: string; uid: string; team_uid: string }) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "teams/user/add",
			data,
		});

		if (!res.error) {
			await refreshBalanceSources();
		}

		return res as IAPIResponse<IStatusResponse>;
	};

	private disableTeamUser: (data: { user_uid: string; uid: string }) => Promise<IAPIResponse<ITeamUserDisableResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			httpMethod: "POST",
			method: "teams/user/disable",
			data,
		})) as IAPIResponse<ITeamUserDisableResponse>;

		if (res.error) return res;

		if (res.data?.status === true) {
			await refreshBalanceSources();
			return res;
		}

		return { error: { code: -1, message: "Unknown error" } };
	};

	private getUsersForTeam: (data?: { uid?: string; team_uid?: string }) => Promise<IAPIResponse<IUserSaved[]>> = async data => {
		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "users/list/team",
			data,
		});
		return res as IAPIResponse<IUserSaved[]>;
	};
}
